/**
 * Keeps the player's game archives in the Origin Private File System so they only import once.
 * Nothing leaves the browser.
 */
import { ARCHIVE_ORDER, type ArchiveName, BlobSource, type NamedSource } from '@d2/formats';
import type { WriterReply, WriterRequest } from './opfs-writer';

const DIR = 'd2-archives';

async function dir(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(DIR, { create: true });
}

export async function storedArchives(): Promise<NamedSource[]> {
  if (!navigator.storage?.getDirectory) return [];
  const d = await dir();
  const out: NamedSource[] = [];
  for (const name of ARCHIVE_ORDER) {
    try {
      const fh = await d.getFileHandle(name);
      const file = await fh.getFile();
      if (file.size > 0) out.push({ name, source: new BlobSource(file), origin: 'browser storage' });
    } catch {
      /* not stored */
    }
  }
  return out;
}

/** A file being written into OPFS. */
interface OpfsWriter {
  write(bytes: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

/** Raised when the browser refuses to store more (quota). */
export class StorageFullError extends Error {
  constructor() {
    super('This browser ran out of storage space for the game archives.');
    this.name = 'StorageFullError';
  }
}

/** Safari reports a full disk as an UnknownError ("unknown transient reason"), not a quota error. */
const isQuota = (e: unknown) => ['QuotaExceededError', 'UnknownError'].includes((e as DOMException)?.name);

/** Chrome, Edge, Firefox and Safari 26+: a writable stream from the main thread. */
export const hasCreateWritable = () => typeof FileSystemFileHandle !== 'undefined' && 'createWritable' in FileSystemFileHandle.prototype;

async function streamWriter(d: FileSystemDirectoryHandle, name: string): Promise<OpfsWriter> {
  const fh = await d.getFileHandle(name, { create: true });
  const w = await fh.createWritable();
  return {
    write: (b) => w.write(b as Uint8Array<ArrayBuffer>),
    close: () => w.close(),
    abort: async () => {
      await w.abort().catch(() => {});
      await d.removeEntry(name).catch(() => {});
    },
  };
}

/** Older Safari: a dedicated worker writing through a sync access handle (see opfs-writer.ts). */
type Request = WriterRequest extends infer R ? (R extends WriterRequest ? Omit<R, 'id'> : never) : never;
let writerWorker: { call(m: Request, transfer?: Transferable[]): Promise<WriterReply> } | null = null;
function worker() {
  if (writerWorker) return writerWorker;
  const w = new Worker(new URL('./opfs-writer.ts', import.meta.url), { type: 'module' });
  const pending = new Map<number, (r: WriterReply) => void>();
  let next = 1;
  w.onmessage = (ev: MessageEvent<WriterReply>) => {
    pending.get(ev.data.id)?.(ev.data);
    pending.delete(ev.data.id);
  };
  writerWorker = {
    call: (m, transfer = []) =>
      new Promise((res) => {
        const id = next++;
        pending.set(id, res);
        w.postMessage({ ...m, id }, transfer);
      }),
  };
  return writerWorker;
}

/** True when the sync-access-handle worker can write OPFS files here. */
export async function canWriteWithWorker(): Promise<boolean> {
  if (typeof Worker === 'undefined') return false;
  return (await worker().call({ op: 'probe' })).ok;
}

async function workerWriter(name: string): Promise<OpfsWriter> {
  const w = worker();
  const check = (r: WriterReply) => {
    if (r.code === 'quota') throw new StorageFullError();
    if (!r.ok) throw new Error(r.error ?? 'storage write failed');
  };
  check(await w.call({ op: 'open', dir: DIR, name }));
  return {
    write: async (b) => {
      const copy = b.slice();
      check(await w.call({ op: 'write', bytes: copy }, [copy.buffer]));
    },
    close: async () => check(await w.call({ op: 'close' })),
    abort: async () => void (await w.call({ op: 'abort' })),
  };
}

/**
 * The worker's sync access handle comes first wherever it exists (Chrome 102+, Firefox 111+,
 * Safari 15.2+): it writes straight to disk. Safari 26's createWritable failed partway through
 * the large archives ("unknown transient reason"), so the stream is only the fallback.
 */
let useWorker: Promise<boolean> | null = null;
const openWriter = async (d: FileSystemDirectoryHandle, name: string) => {
  // A worker that never answers (failed to load) counts as missing after 3 s.
  useWorker ??= Promise.race([canWriteWithWorker().catch(() => false), new Promise<boolean>((r) => setTimeout(() => r(false), 3000))]);
  return (await useWorker) || !hasCreateWritable() ? workerWriter(name) : streamWriter(d, name);
};

/** Streams one archive into OPFS. `onProgress` gets bytes written so far. */
export async function storeArchive(src: NamedSource, onProgress: (written: number) => void): Promise<void> {
  const d = await dir();
  const tmp = `${src.name}.partial`;
  const CHUNK = 8 << 20;
  const copy = async (name: string, size: number, read: (off: number, len: number) => Promise<Uint8Array>, progress?: (n: number) => void) => {
    const w = await openWriter(d, name);
    try {
      for (let off = 0; off < size; off += CHUNK) {
        const bytes = await read(off, Math.min(CHUNK, size - off));
        await w.write(bytes);
        progress?.(off + bytes.length);
      }
      await w.close();
    } catch (e) {
      await w.abort();
      throw isQuota(e) ? new StorageFullError() : e;
    }
  };
  await copy(tmp, src.source.size, (off, len) => src.source.read(off, len), onProgress);
  // Only a complete file gets the archive's name: move it when supported, otherwise copy it.
  const fh = await d.getFileHandle(tmp);
  // A full disk can end a write short without an error (Safari): never keep a cut-off copy.
  if ((await fh.getFile()).size !== src.source.size) {
    await d.removeEntry(tmp).catch(() => {});
    throw new StorageFullError();
  }
  // move(dir, name): Safari requires both arguments (Chrome also takes the name alone).
  const move = (fh as unknown as { move?: (dir: FileSystemDirectoryHandle, name: string) => Promise<void> }).move;
  if (move) await move.call(fh, d, src.name);
  else {
    const file = await fh.getFile();
    await copy(src.name, file.size, async (off, len) => new Uint8Array(await file.slice(off, off + len).arrayBuffer()));
    await d.removeEntry(tmp);
    if ((await (await d.getFileHandle(src.name)).getFile()).size !== file.size) {
      await d.removeEntry(src.name).catch(() => {});
      throw new StorageFullError();
    }
  }
}

export async function forgetArchives(): Promise<void> {
  const root = await navigator.storage.getDirectory();
  await root.removeEntry(DIR, { recursive: true }).catch(() => {});
}

export async function storageEstimate(): Promise<{ usage: number; quota: number }> {
  const e = await navigator.storage.estimate();
  return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
}

/** Asks the browser not to evict the archives under storage pressure (best effort). */
export async function requestPersistence(): Promise<boolean> {
  try {
    return (await navigator.storage.persisted?.()) || ((await navigator.storage.persist?.()) ?? false);
  } catch {
    return false;
  }
}

export function isArchiveName(n: string): n is ArchiveName {
  return (ARCHIVE_ORDER as readonly string[]).includes(n);
}
